import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  FileCheck2, Plus, RefreshCw, Search, Trash2, Edit3,
  Building2, Globe, AlertTriangle, Calendar, X, Receipt,
  CheckCircle2, FileText, Upload,
} from "lucide-react";

const PLACEMENT_OPTIONS = [
  { key: "with_us", label: "With us (we placed worker)",   icon: Building2, color: "emerald",
    helper: "Worker is at one of our managed clients (Kenvue, Ceva, etc.) — WORKER pays Mercy." },
  { key: "others",  label: "Other employer",              icon: Globe,    color: "violet",
    helper: "Worker works at an external company — that CLIENT pays Mercy." },
];

const PAYMENT_PLANS = [
  { key: "full",        label: "Full payment upfront", desc: "One lump sum before renewing" },
  { key: "fifty_fifty", label: "50% deposit + balance", desc: "Half upfront, half after renewal" },
  { key: "monthly",     label: "Monthly deduction",     desc: "Spread across multiple months" },
];

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Cheque", "Card", "Online", "Other"];

const STATUSES = ["Pending", "In progress", "Renewed", "Cancelled"];

const STATUS_PILL = {
  "Pending":     "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "In progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Renewed":     "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Cancelled":   "bg-red-500/15 text-red-300 border-red-500/30",
};

const EXPIRY_PILL = {
  ok:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  soon:    "bg-amber-500/15 text-amber-300 border-amber-500/30",
  urgent:  "bg-orange-500/15 text-orange-300 border-orange-500/30",
  expired: "bg-red-500/15 text-red-300 border-red-500/30",
  unknown: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const EXPIRY_LABEL = {
  ok: "Valid", soon: "Expiring <60d", urgent: "Expiring <30d",
  expired: "EXPIRED", unknown: "—",
};

function fmtMoney(n) {
  return `RM ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}
async function fileToBase64(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || "").split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function Permits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [placementFilter, setPlacementFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null); // case being viewed
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (placementFilter) params.placement = placementFilter;
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/permits/stats"),
        api.get("/permits", { params }),
      ]);
      setStats(s);
      setRows(r || []);
    } catch (e) {
      toast.error("Failed to load permits");
    } finally {
      setBusy(false);
    }
  }, [q, statusFilter, placementFilter]);

  useEffect(() => { load(); }, [load]);

  const removeCase = async (c) => {
    if (!window.confirm(`Delete permit case for "${c.worker_name}"?\n\nAll payment records will also be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/permits/${c.id}`);
      toast.success("Case deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-sky-600/20 border border-sky-600/40 flex items-center justify-center">
            <FileCheck2 className="w-5 h-5 text-sky-300" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Permits</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Foreign-worker visa &amp; permit renewals — track who pays, payment plans, receipts.
            </p>
          </div>
          <div className="ml-auto">
            <Button onClick={() => setCreating(true)}
              className="bg-sky-600 hover:bg-sky-500 text-sky-50" data-testid="permits-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New case
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <StatCard label="Total cases" value={stats.total} icon={FileText}
                      active={!statusFilter && !placementFilter}
                      onClick={() => { setStatusFilter(""); setPlacementFilter(""); }}
                      testid="permits-stat-total" />
            <StatCard label="Pending" value={stats.pending} icon={Calendar} tone="slate"
                      active={statusFilter === "Pending"}
                      onClick={() => setStatusFilter("Pending")} testid="permits-stat-pending" />
            <StatCard label="In progress" value={stats.in_progress} icon={RefreshCw} tone="amber"
                      active={statusFilter === "In progress"}
                      onClick={() => setStatusFilter("In progress")} testid="permits-stat-inprogress" />
            <StatCard label="Expiry alerts" value={stats.expiry_alert} icon={AlertTriangle} tone="red"
                      testid="permits-stat-expiry" />
            <StatCard label="Outstanding" value={fmtMoney(stats.outstanding)} icon={Receipt} tone="orange"
                      testid="permits-stat-outstanding" />
          </div>
        )}

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search worker, passport, or external client…"
                   className="bg-[#0a0e1a] border-slate-800 pl-9 h-9" data-testid="permits-search" />
          </div>
          <select value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)}
                  className="bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm">
            <option value="">All placements</option>
            <option value="with_us">With us</option>
            <option value="others">Others</option>
          </select>
          {(q || statusFilter || placementFilter) && (
            <Button size="sm" variant="ghost" className="h-9 text-xs text-slate-400"
                    onClick={() => { setQ(""); setStatusFilter(""); setPlacementFilter(""); }}>
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} className="h-9 border-slate-700">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="space-y-2">
          {rows.length === 0 && (
            <div className="rounded-lg border hairline surface-1 text-center text-sm text-slate-500 py-12">
              {q || statusFilter || placementFilter
                ? "No cases match your filters."
                : "No permit cases yet. Click \"New case\" to add the first one."}
            </div>
          )}
          {rows.map((c) => (
            <CaseCard key={c.id} c={c} onOpen={() => setDetail(c)} />
          ))}
        </div>
      </div>

      <CreateOrEditDialog
        open={creating || !!editing}
        onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); } }}
        record={editing}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />

      <CaseDetailDialog
        open={!!detail}
        onOpenChange={(v) => { if (!v) setDetail(null); }}
        caseId={detail?.id}
        isAdmin={isAdmin}
        onEdit={(c) => { setDetail(null); setEditing(c); }}
        onDelete={removeCase}
        onChanged={load}
      />
    </Layout>
  );
}

// ============================================================================
// Card on the main list
// ============================================================================
function CaseCard({ c, onOpen }) {
  const isWithUs = c.placement_type === "with_us";
  const expState = c.expiry?.state || "unknown";
  return (
    <button onClick={onOpen}
      className="w-full text-left rounded-lg border hairline surface-1 hover:bg-white/[0.02] transition p-3"
      data-testid={`permit-card-${c.id}`}>
      <div className="flex items-start gap-3">
        {/* Placement icon */}
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0
          ${isWithUs ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                     : "bg-violet-500/10 border-violet-500/30 text-violet-300"}`}>
          {isWithUs ? <Building2 className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">{c.worker_name}</span>
            {c.worker_code && <span className="text-[10px] text-slate-500 num">· {c.worker_code}</span>}
            {c.passport_no && <span className="text-[10px] text-slate-500 num">· {c.passport_no}</span>}
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_PILL[c.status] || ""}`}>
              {c.status}
            </span>
            {c.permit_expiry_date && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${EXPIRY_PILL[expState]}`}>
                {EXPIRY_LABEL[expState]}
                {c.expiry?.days_remaining !== null && c.expiry?.days_remaining !== undefined &&
                  ` · ${c.expiry.days_remaining}d`}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {isWithUs ? (
              <>🏢 {c.managed_company_name || "(no managed client)"} · <span className="text-emerald-300">worker pays</span></>
            ) : (
              <>🌐 {c.external_client_name || "(external client)"} · <span className="text-violet-300">client pays</span></>
            )}
            {c.permit_expiry_date && <> · Expires {fmtDate(c.permit_expiry_date)}</>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold num">{fmtMoney(c.total_fee)}</div>
          <div className="text-[10px] num text-slate-500">
            paid: <span className="text-emerald-300">{fmtMoney(c.paid_total)}</span>
            {c.balance_owing > 0 && <>
              {" · "}due: <span className="text-amber-300">{fmtMoney(c.balance_owing)}</span>
            </>}
          </div>
        </div>
      </div>
    </button>
  );
}

function StatCard({ label, value, icon: Icon, tone = "slate", onClick, active, testid }) {
  const toneCls = {
    slate:   "border-slate-800 bg-slate-900/40 text-slate-200",
    red:     "border-red-500/30 bg-red-500/10 text-red-200",
    orange:  "border-orange-500/30 bg-orange-500/10 text-orange-200",
    amber:   "border-amber-500/30 bg-amber-500/10 text-amber-200",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  }[tone];
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-lg border px-3 py-3 text-left transition ${toneCls}
        ${active ? "ring-2 ring-sky-500/60" : "hover:brightness-110"}
        ${!onClick ? "cursor-default" : ""}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4 opacity-70" />
        <div className="text-lg font-bold num">{value}</div>
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">{label}</div>
    </button>
  );
}

// ============================================================================
// CREATE / EDIT dialog
// ============================================================================
function CreateOrEditDialog({ open, onOpenChange, record, onSaved }) {
  const isEdit = !!record;
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  function emptyForm() {
    return {
      worker_name: "", worker_code: "", passport_no: "", nationality: "",
      permit_expiry_date: "",
      placement_type: "with_us",
      managed_company_id: "",
      external_client_name: "",
      total_fee: "",
      payment_plan: "full",
      monthly_installment: "",
      plan_start_date: "",
      notes: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    api.get("/roster/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
    if (isEdit) {
      setForm({
        worker_name: record.worker_name || "",
        worker_code: record.worker_code || "",
        passport_no: record.passport_no || "",
        nationality: record.nationality || "",
        permit_expiry_date: record.permit_expiry_date || "",
        placement_type: record.placement_type || "with_us",
        managed_company_id: record.managed_company_id || "",
        external_client_name: record.external_client_name || "",
        total_fee: record.total_fee || "",
        payment_plan: record.payment_plan || "full",
        monthly_installment: record.monthly_installment || "",
        plan_start_date: record.plan_start_date || "",
        notes: record.notes || "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, isEdit, record]);

  const save = async () => {
    if (!form.worker_name.trim()) { toast.error("Worker name required"); return; }
    if (!form.total_fee || Number(form.total_fee) <= 0) { toast.error("Total fee required"); return; }
    if (form.placement_type === "with_us" && !form.managed_company_id) {
      toast.error("Pick the managed client where this worker is placed"); return;
    }
    if (form.placement_type === "others" && !form.external_client_name.trim()) {
      toast.error("Enter the external client name"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_fee: Number(form.total_fee || 0),
        monthly_installment: Number(form.monthly_installment || 0),
      };
      if (payload.placement_type === "others") payload.managed_company_id = null;
      if (isEdit) {
        await api.put(`/permits/${record.id}`, payload);
        toast.success("Case updated");
      } else {
        await api.post("/permits", payload);
        toast.success("Case created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-sky-300" />
            {isEdit ? "Edit permit case" : "New permit case"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Worker info */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 num">Worker</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <Input value={form.worker_name} onChange={(e) => setForm({ ...form, worker_name: e.target.value })}
                placeholder="Full name *" className="bg-[#0a0e1a] border-slate-800" autoFocus
                data-testid="permits-form-name" />
              <Input value={form.worker_code} onChange={(e) => setForm({ ...form, worker_code: e.target.value })}
                placeholder="Worker code (optional)" className="bg-[#0a0e1a] border-slate-800 num" />
              <Input value={form.passport_no} onChange={(e) => setForm({ ...form, passport_no: e.target.value })}
                placeholder="Passport number" className="bg-[#0a0e1a] border-slate-800 num" />
              <Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                placeholder="Nationality (e.g. Indonesia)" className="bg-[#0a0e1a] border-slate-800" />
              <div>
                <Label className="text-[10px] text-slate-500">Permit expires</Label>
                <Input type="date" value={form.permit_expiry_date}
                  onChange={(e) => setForm({ ...form, permit_expiry_date: e.target.value })}
                  className="bg-[#0a0e1a] border-slate-800 mt-0.5 num" />
              </div>
            </div>
          </div>

          {/* Placement */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 num">Placement</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {PLACEMENT_OPTIONS.map((p) => {
                const Icon = p.icon;
                const active = form.placement_type === p.key;
                return (
                  <button key={p.key}
                    onClick={() => setForm({ ...form, placement_type: p.key })}
                    type="button"
                    data-testid={`permits-form-placement-${p.key}`}
                    className={`text-left rounded border px-3 py-2.5 transition
                      ${active
                        ? p.color === "emerald"
                          ? "border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500/30"
                          : "border-violet-500 bg-violet-500/10 ring-1 ring-violet-500/30"
                        : "border-slate-800 bg-[#0a0e1a] hover:border-slate-600"}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Icon className="w-4 h-4" /> {p.label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 leading-snug">{p.helper}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-2">
              {form.placement_type === "with_us" ? (
                <div>
                  <Label className="text-[10px] text-slate-500">Managed client</Label>
                  <select value={form.managed_company_id}
                    onChange={(e) => setForm({ ...form, managed_company_id: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-0.5"
                    data-testid="permits-form-managed-client">
                    <option value="">— pick a client —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <Label className="text-[10px] text-slate-500">External client name</Label>
                  <Input value={form.external_client_name}
                    onChange={(e) => setForm({ ...form, external_client_name: e.target.value })}
                    placeholder="e.g. ABC Plantation Sdn Bhd"
                    className="bg-[#0a0e1a] border-slate-800 mt-0.5"
                    data-testid="permits-form-external-client" />
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 num">Pricing</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <div>
                <Label className="text-[10px] text-slate-500">Total fee (RM) *</Label>
                <Input type="number" value={form.total_fee}
                  onChange={(e) => setForm({ ...form, total_fee: e.target.value })}
                  placeholder="e.g. 3200" className="bg-[#0a0e1a] border-slate-800 mt-0.5 num"
                  data-testid="permits-form-fee" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Payment plan</Label>
                <select value={form.payment_plan}
                  onChange={(e) => setForm({ ...form, payment_plan: e.target.value })}
                  className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-0.5"
                  data-testid="permits-form-plan">
                  {PAYMENT_PLANS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
              {form.payment_plan === "monthly" && (
                <>
                  <div>
                    <Label className="text-[10px] text-slate-500">Monthly installment (RM)</Label>
                    <Input type="number" value={form.monthly_installment}
                      onChange={(e) => setForm({ ...form, monthly_installment: e.target.value })}
                      placeholder="e.g. 400" className="bg-[#0a0e1a] border-slate-800 mt-0.5 num" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Plan start date</Label>
                    <Input type="date" value={form.plan_start_date}
                      onChange={(e) => setForm({ ...form, plan_start_date: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 mt-0.5 num" />
                  </div>
                </>
              )}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              {PAYMENT_PLANS.find((p) => p.key === form.payment_plan)?.desc}
            </div>
          </div>

          <div>
            <Label className="text-[10px] text-slate-500">Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional info"
              className="bg-[#0a0e1a] border-slate-800 mt-0.5" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-sky-600 hover:bg-sky-500 text-sky-50" data-testid="permits-form-save">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CASE DETAIL dialog (with payments list + Add Payment)
// ============================================================================
function CaseDetailDialog({ open, onOpenChange, caseId, isAdmin, onEdit, onDelete, onChanged }) {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data: d } = await api.get(`/permits/${caseId}`);
      setData(d);
    } catch {
      toast.error("Failed to load case");
    }
  }, [caseId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl">
          <div className="text-center py-12 text-sm text-slate-500">Loading…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const isWithUs = data.placement_type === "with_us";
  const fullyPaid = data.balance_owing === 0 && data.total_fee > 0;

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment? Receipt is kept in storage for audit.")) return;
    try {
      await api.delete(`/permits/${data.id}/payments/${pid}`);
      toast.success("Payment removed");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markRenewed = async () => {
    if (!window.confirm("Mark this permit as renewed?")) return;
    try {
      await api.put(`/permits/${data.id}`, {
        status: "Renewed",
        renewal_completed_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Marked renewed");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto"
                       data-testid="permits-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span className={`w-7 h-7 rounded border flex items-center justify-center
                ${isWithUs ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                            : "bg-violet-500/10 border-violet-500/30 text-violet-300"}`}>
                {isWithUs ? <Building2 className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
              </span>
              <span className="truncate">{data.worker_name}</span>
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_PILL[data.status] || ""}`}>
                {data.status}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Worker + placement summary */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Info label="Passport" value={data.passport_no || "—"} />
              <Info label="Nationality" value={data.nationality || "—"} />
              <Info label="Permit expires" value={fmtDate(data.permit_expiry_date)} />
              <Info label="Renewal completed" value={fmtDate(data.renewal_completed_date)} />
              <Info label="Placement"
                value={isWithUs ? `🏢 With us · ${data.managed_company_name || "—"}` : `🌐 Others · ${data.external_client_name || "—"}`} />
              <Info label="Payer" value={isWithUs ? "Worker pays Mercy" : "Client pays Mercy"} />
            </div>

            {/* Money summary */}
            <div className="rounded-lg border hairline bg-[#0a0e1a] px-4 py-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total fee</div>
                  <div className="text-lg font-bold num">{fmtMoney(data.total_fee)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400">Paid</div>
                  <div className="text-lg font-bold num text-emerald-300">{fmtMoney(data.paid_total)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-400">Balance</div>
                  <div className={`text-lg font-bold num ${data.balance_owing > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                    {fmtMoney(data.balance_owing)}
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-2 text-center">
                Plan: <span className="text-slate-300">
                  {PAYMENT_PLANS.find((p) => p.key === data.payment_plan)?.label || data.payment_plan}
                </span>
                {data.payment_plan === "monthly" && data.monthly_installment > 0 && (
                  <> · {fmtMoney(data.monthly_installment)}/month{data.plan_start_date && ` from ${fmtDate(data.plan_start_date)}`}</>
                )}
              </div>
            </div>

            {/* Payments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 num">
                  Payments · {data.payments?.length || 0}
                </div>
                {data.status !== "Renewed" && (
                  <Button size="sm" onClick={() => setAddOpen(true)}
                    className="h-7 bg-emerald-600 hover:bg-emerald-500 text-emerald-50 text-xs"
                    data-testid="permits-add-payment-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add payment
                  </Button>
                )}
              </div>
              {(!data.payments || data.payments.length === 0) ? (
                <div className="text-xs text-slate-500 text-center py-6 border border-dashed hairline rounded">
                  No payments recorded yet.
                </div>
              ) : (
                <div className="space-y-1">
                  {data.payments.map((p) => (
                    <PaymentRow key={p.id} p={p} caseId={data.id} isAdmin={isAdmin} onDelete={removePayment} />
                  ))}
                </div>
              )}
            </div>

            {data.notes && (
              <div className="text-xs">
                <Label className="text-[10px] text-slate-500">Notes</Label>
                <div className="mt-1 p-2 rounded bg-[#0a0e1a] border border-slate-800 text-slate-300 whitespace-pre-wrap">
                  {data.notes}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            {data.status !== "Renewed" && fullyPaid && (
              <Button onClick={markRenewed} className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
                      data-testid="permits-mark-renewed">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark renewed
              </Button>
            )}
            <Button variant="outline" onClick={() => onEdit?.(data)} className="border-slate-700"
                    data-testid="permits-detail-edit">
              <Edit3 className="w-4 h-4 mr-1" /> Edit
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={() => onDelete?.(data)}
                      className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                      data-testid="permits-detail-delete">
                <Trash2 className="w-4 h-4 mr-1" /> Delete case
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="ml-auto">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddPaymentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        caseId={data.id}
        balance={data.balance_owing}
        suggestedAmount={data.payment_plan === "monthly" ? data.monthly_installment : data.balance_owing}
        onSaved={() => { setAddOpen(false); load(); onChanged?.(); }}
      />
    </>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className="mt-0.5 text-slate-200">{value}</div>
    </div>
  );
}

function PaymentRow({ p, caseId, isAdmin, onDelete }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("mercy_token");
  const receiptUrl = `${BACKEND_URL}/api/permits/${caseId}/payments/${p.id}/receipt`;
  const openReceipt = async () => {
    // Fetch with auth header → open as blob (Authorization can't be set on plain <a>).
    try {
      const res = await fetch(receiptUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Could not open receipt");
    }
  };
  return (
    <div className="rounded border hairline bg-[#0a0e1a]/40 px-3 py-2 flex items-center gap-3 text-xs">
      <Receipt className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold num">{fmtMoney(p.amount)}</span>
          <span className="text-slate-500">·</span>
          <span className="num">{fmtDate(p.payment_date)}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-300">{p.method}</span>
          {p.reference && <span className="text-slate-500 num text-[10px]">({p.reference})</span>}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {p.recorded_by_name && <>by {p.recorded_by_name} · </>}
          📎 {p.receipt_filename || "receipt"}
        </div>
      </div>
      <Button size="sm" variant="ghost" className="h-7 text-xs text-sky-300" onClick={openReceipt}
              data-testid={`permits-receipt-${p.id}`}>
        <FileText className="w-3.5 h-3.5 mr-1" /> View
      </Button>
      {isAdmin && (
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-300"
                onClick={() => onDelete?.(p.id)} data-testid={`permits-payment-delete-${p.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// ADD PAYMENT dialog (with mandatory receipt)
// ============================================================================
function AddPaymentDialog({ open, onOpenChange, caseId, balance, suggestedAmount, onSaved }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "",
    method: "Bank Transfer",
    reference: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        payment_date: new Date().toISOString().slice(0, 10),
        amount: suggestedAmount ? String(suggestedAmount) : "",
        method: "Bank Transfer", reference: "", notes: "",
      });
      setFile(null);
    }
  }, [open, suggestedAmount]);

  const save = async () => {
    if (!form.payment_date) { toast.error("Date required"); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    if (!file) { toast.error("Receipt attachment is required"); return; }
    setBusy(true);
    try {
      const data_base64 = await fileToBase64(file);
      await api.post(`/permits/${caseId}/payments`, {
        ...form,
        amount: amt,
        receipt: {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          data_base64,
        },
      });
      toast.success("Payment recorded");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-300" /> Record payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          {balance > 0 && (
            <div className="text-[11px] text-slate-400 bg-slate-900/60 border border-slate-800 rounded px-2 py-1.5">
              Balance owing: <span className="text-amber-300 num font-semibold">{fmtMoney(balance)}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-slate-500">Date</Label>
              <Input type="date" value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-0.5 num"
                data-testid="permits-payment-date" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Amount (RM) *</Label>
              <Input type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-0.5 num"
                data-testid="permits-payment-amount" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Method</Label>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-0.5"
                data-testid="permits-payment-method">
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Reference / receipt no.</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. BT-2026-0517"
                className="bg-[#0a0e1a] border-slate-800 mt-0.5 num" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 flex items-center gap-1.5">
              Receipt (PDF or image) <span className="text-red-400">*</span>
            </Label>
            <Input type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={`bg-[#0a0e1a] mt-0.5 file:text-slate-300 ${file ? "border-emerald-700" : "border-amber-700/60"}`}
              data-testid="permits-payment-receipt" />
            <div className={`text-[10px] mt-1 ${file ? "text-emerald-400" : "text-amber-300/80"}`}>
              {file ? `✓ ${file.name}` : <><Upload className="w-3 h-3 inline mr-1" />Required for our records</>}
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-0.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
            data-testid="permits-payment-save">
            {busy ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
